/* ============================================================
   THE FLOWER LAB — Día y franja de entrega
   ============================================================
   La flor se compone el mismo día que sale, así que el momento de
   la entrega no es un detalle del final: condiciona el trabajo del
   estudio. Por eso se pregunta en el carrito, antes de cobrar, y no
   después en una nota suelta.

   Lo elegido viaja con el pedido como atributos del carrito:

     Fecha de entrega   "martes 25 de agosto de 2026"
     Hora de entrega    "03:00 PM - 07:00 PM"
     _fecha_entrega     "2026-08-25"

   El tercero empieza con guion bajo, así que Shopify no se lo enseña
   al cliente. Es el que permite volver a marcar el día en el
   calendario cuando el panel del carrito se rehace, sin tener que
   interpretar el texto en español.

   ------------------------------------------------------------
   Las dos reglas del mismo día
   ------------------------------------------------------------
   1. La hora de corte (12:00) cierra el día entero: a partir de esa
      hora, hoy desaparece del calendario y el primer día posible es
      mañana.

   2. Antes del corte hoy sí se puede, pero no en cualquier franja:
      solo en las marcadas con '| hoy' en el customizer. Con las de
      ahora eso significa la de la tarde y ninguna más — pedir a las
      9 de la mañana no hace que quepa la franja que arranca a esa
      misma hora.

   Las franjas se imprimen todas desde Liquid y es este archivo el
   que esconde las que no tocan: saber si el día elegido es "hoy"
   depende del reloj de quien compra, no del servidor.

   ------------------------------------------------------------
   Por qué los listeners van delegados en `document`
   ------------------------------------------------------------
   El panel del carrito se reemplaza entero al agregar un producto y
   cada vez que el theme recalcula totales. Enganchando campo por
   campo, los que salen de ese reemplazo quedan mudos.
   ============================================================ */
(function () {
  'use strict';

  var ATTR_FECHA = 'Fecha de entrega';
  var ATTR_HORA = 'Hora de entrega';
  var ATTR_ISO = '_fecha_entrega';

  // ---- Fechas, siempre en la hora de quien compra (nunca UTC) ----

  function dosDigitos(n) {
    return (n < 10 ? '0' : '') + n;
  }

  function aISO(d) {
    return d.getFullYear() + '-' + dosDigitos(d.getMonth() + 1) + '-' + dosDigitos(d.getDate());
  }

  /**
   * "2026-08-25" → Date local.
   *
   * A propósito no se usa `new Date('2026-08-25')`: esa forma se lee como
   * UTC y en México adelanta el día, así que el calendario marcaba uno y
   * el pedido registraba otro.
   */
  function desdeISO(iso) {
    var p = String(iso || '').split('-');
    if (p.length !== 3) return null;

    var d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
    return isNaN(d.getTime()) ? null : d;
  }

  function sumarDias(d, n) {
    var x = new Date(d.getTime());
    x.setDate(x.getDate() + n);
    return x;
  }

  function aMinutos(hhmm) {
    var p = String(hhmm || '').split(':');
    var h = parseInt(p[0], 10);
    if (isNaN(h)) return 0;
    return h * 60 + (parseInt(p[1], 10) || 0);
  }

  function legible(d) {
    try {
      return d.toLocaleDateString('es-MX', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    } catch (e) {
      return aISO(d);
    }
  }

  // ---- Reglas ----

  function configDe(raiz) {
    var d = raiz.dataset;

    var cerrados = String(d.cerrados || '')
      .split(',')
      .map(function (x) {
        return parseInt(x.trim(), 10);
      })
      .filter(function (n) {
        return !isNaN(n) && n >= 0 && n <= 6;
      });

    return {
      diasMax: Math.max(0, parseInt(d.diasMax, 10) || 30),
      corte: aMinutos(d.corte || '12:00'),
      cerrados: cerrados,
      msgSinHoras: d.msgSinHoras || 'Ya no quedan horarios para ese día. Elige el siguiente.',
      msgCerrado: d.msgCerrado || 'Ese día no hacemos entregas. Elige otro.',
      msgListo: d.msgListo || 'Entregamos el {fecha} en el horario de {hora}.',
      msgYaPaso: d.msgYaPaso || 'Ese día ya pasó. Elige uno a partir del primero disponible.',
      msgMuyLejos: d.msgMuyLejos || 'Todavía no tomamos pedidos tan adelantados. Elige un día más cercano.',
    };
  }

  function franjasDe(raiz) {
    return Array.prototype.slice.call(raiz.querySelectorAll('[data-fl-entrega-franja]'));
  }

  function esDiaCerrado(cfg, d) {
    return cfg.cerrados.indexOf(d.getDay()) !== -1;
  }

  /**
   * ¿Ese día queda fuera del rango que se puede pedir?
   *
   * `min` y `max` solo apagan los días en el calendario que abre el
   * teléfono. Escribiendo la fecha a mano —tecleando en el campo— el
   * navegador la acepta igual y se limita a marcarla inválida por dentro,
   * así que sin esta comprobación se colaba un 01/01/2020 con el horario
   * completo disponible.
   *
   * Se comparan los textos tal cual: en formato AAAA-MM-DD el orden
   * alfabético es el orden cronológico.
   */
  function fueraDeRango(campoFecha, iso) {
    if (!iso) return '';
    if (campoFecha.min && iso < campoFecha.min) return 'antes';
    if (campoFecha.max && iso > campoFecha.max) return 'despues';
    return '';
  }

  /**
   * El primer día que de verdad se puede elegir.
   *
   * Hoy solo cuenta si no ha pasado la hora de corte Y existe al menos
   * una franja marcada para el mismo día. Si el customizer no marcó
   * ninguna, hoy no entra aunque sean las nueve de la mañana.
   */
  function primerDia(raiz, cfg, ahora) {
    var d = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());

    var pasoElCorte = ahora.getHours() * 60 + ahora.getMinutes() >= cfg.corte;

    var hayFranjaHoy = franjasDe(raiz).some(function (f) {
      return f.dataset.hoy === 'si';
    });

    if (pasoElCorte || !hayFranjaHoy) d = sumarDias(d, 1);

    for (var i = 0; i <= cfg.diasMax; i++) {
      if (!esDiaCerrado(cfg, d)) return d;
      d = sumarDias(d, 1);
    }
    return d;
  }

  /**
   * Deja el calendario limitado a los días que de verdad se pueden pedir.
   *
   * Se vuelve a calcular en cada cambio, no solo al arrancar: una pestaña
   * abierta desde la mañana cruza la hora de corte —y en algún momento la
   * medianoche— y el rango de hace horas ya no vale.
   */
  function ponerRango(raiz, cfg, ahora) {
    var campo = raiz.querySelector('[data-fl-entrega-fecha]');
    if (!campo) return;

    var desde = primerDia(raiz, cfg, ahora);
    campo.min = aISO(desde);
    campo.max = aISO(sumarDias(desde, cfg.diasMax));
  }

  // ---- Pintar ----

  /** El campo del día se ve gris mientras no hay elección. Lo lee el CSS. */
  function marcarElegido(campo) {
    if (campo.value) campo.dataset.elegido = 'si';
    else delete campo.dataset.elegido;
  }

  /**
   * Muestra las franjas que caben en el día elegido y esconde el resto.
   *
   * Para hoy solo valen las marcadas con '| hoy'. Para cualquier otro día
   * valen todas. Y si el día no sirve —vacío, fuera de rango o cerrado—
   * no se ofrece ninguna: elegir hora de un día imposible no lleva a
   * ningún lado.
   */
  function pintarFranjas(raiz, cfg, ahora) {
    var campo = raiz.querySelector('[data-fl-entrega-fecha]');
    if (!campo) return;

    var iso = campo.value;
    var dia = desdeISO(iso);
    var esHoy = iso === aISO(ahora);

    var diaSirve = !!iso && !fueraDeRango(campo, iso) && !!dia && !esDiaCerrado(cfg, dia);

    franjasDe(raiz).forEach(function (franja) {
      var sirve = diaSirve && (!esHoy || franja.dataset.hoy === 'si');
      var radio = franja.querySelector('[data-fl-entrega-hora]');

      franja.hidden = !sirve;
      if (!radio) return;

      radio.disabled = !sirve;
      // Una franja que deja de servir no puede quedarse marcada por
      // debajo: se iría al pedido sin que nadie la vea en pantalla.
      if (!sirve && radio.checked) radio.checked = false;
    });
  }

  /** La franja marcada ahora mismo, si alguna. */
  function franjaElegida(raiz) {
    var radio = raiz.querySelector('[data-fl-entrega-hora]:checked');
    return radio && !radio.disabled ? radio.value : '';
  }

  function mostrar(raiz, estado, texto) {
    var salida = raiz.querySelector('[data-fl-entrega-resultado]');
    if (!salida) return;

    if (!texto) {
      salida.hidden = true;
      salida.textContent = '';
      delete salida.dataset.estado;
      return;
    }

    salida.textContent = texto;
    salida.dataset.estado = estado;
    salida.hidden = false;
  }

  /** Repasa en qué punto va el paso y lo cuenta: en pantalla y hacia afuera. */
  function refrescar(raiz, cfg, ahora) {
    var campo = raiz.querySelector('[data-fl-entrega-fecha]');
    if (!campo) return null;

    marcarElegido(campo);

    var iso = campo.value;
    var dia = desdeISO(iso);
    var fuera = fueraDeRango(campo, iso);
    var hora = franjaElegida(raiz);

    var quedanFranjas = franjasDe(raiz).some(function (f) {
      return !f.hidden;
    });

    if (fuera) {
      mostrar(raiz, 'aviso', fuera === 'antes' ? cfg.msgYaPaso : cfg.msgMuyLejos);
    } else if (iso && dia && esDiaCerrado(cfg, dia)) {
      mostrar(raiz, 'aviso', cfg.msgCerrado);
    } else if (iso && !quedanFranjas) {
      mostrar(raiz, 'aviso', cfg.msgSinHoras);
    } else if (iso && hora) {
      mostrar(
        raiz,
        'listo',
        String(cfg.msgListo).replace('{fecha}', legible(dia)).replace('{hora}', hora)
      );
    } else {
      mostrar(raiz, '', '');
    }

    // Una fecha fuera de rango se queda a la vista en el campo —hay que
    // poder ver qué se escribió para corregirlo— pero no viaja al pedido.
    var detalle = {
      iso: fuera ? '' : iso || '',
      fecha: fuera || !dia ? '' : legible(dia),
      hora: hora,
      completa: !!(iso && hora && !fuera),
    };

    anunciar(detalle, raiz);
    return detalle;
  }

  /**
   * Publica el estado por dos caminos: el evento para quien ya escucha, y
   * el dato anotado para quien se cargue después.
   */
  function anunciar(detalle, raiz) {
    window.FlowerLabEntrega.ultimo = detalle;

    (raiz || document).dispatchEvent(
      new CustomEvent('flowerlab:entrega:cambiada', {
        bubbles: true,
        detail: detalle,
      })
    );
  }

  // ---- Guardar en el pedido ----

  var guardando = null;

  /**
   * Deja lo elegido en los atributos del carrito, que es lo que después
   * aparece en el pedido. Se llama solo cuando la persona cambia algo:
   * repintar las franjas no toca el carrito.
   */
  function guardar(detalle) {
    var atributos = {};
    atributos[ATTR_ISO] = detalle.iso;
    atributos[ATTR_FECHA] = detalle.fecha;
    atributos[ATTR_HORA] = detalle.hora;

    // Si llegan dos cambios seguidos, manda el último.
    if (guardando) guardando.abort();
    guardando = new AbortController();

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: atributos }),
      signal: guardando.signal,
    })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        // No se avisa en pantalla: lo elegido sigue puesto y el siguiente
        // cambio lo reintenta. Cortar la compra por esto sería peor.
        console.warn('[flowerlab-entrega] no se pudo guardar', err);
      })
      .then(function () {
        guardando = null;
      });
  }

  // ---- Arranque de cada paso de entrega ----

  function iniciar(raiz) {
    if (!raiz || raiz.dataset.flEntregaInit === '1') return;

    var campo = raiz.querySelector('[data-fl-entrega-fecha]');
    if (!campo || !franjasDe(raiz).length) return;

    raiz.dataset.flEntregaInit = '1';

    var cfg = configDe(raiz);
    var ahora = new Date();

    // El rango del calendario se calcula acá y no en Liquid: depende de la
    // hora de quien compra, no de la del servidor.
    ponerRango(raiz, cfg, ahora);

    // Una fecha guardada que quedó atrás —el carrito lleva días abierto—
    // se descarta en vez de arrastrarse hasta el pedido.
    if (fueraDeRango(campo, campo.value)) campo.value = '';

    pintarFranjas(raiz, cfg, ahora);
    refrescar(raiz, cfg, ahora);
  }

  function iniciarTodos(ambito) {
    var raiz = ambito && ambito.querySelectorAll ? ambito : document;

    var nodos = raiz.querySelectorAll('[data-fl-entrega]');
    for (var i = 0; i < nodos.length; i++) iniciar(nodos[i]);

    if (raiz !== document && raiz.matches && raiz.matches('[data-fl-entrega]')) iniciar(raiz);
  }

  window.FlowerLabEntrega = {
    iniciarTodos: iniciarTodos,
    ultimo: null,
  };

  // ---- Listeners delegados ----

  document.addEventListener('change', function (evento) {
    var campo = evento.target;
    if (!campo || !campo.matches) return;

    var esFecha = campo.matches('[data-fl-entrega-fecha]');
    var esHora = campo.matches('[data-fl-entrega-hora]');
    if (!esFecha && !esHora) return;

    var raiz = campo.closest('[data-fl-entrega]');
    if (!raiz) return;

    var cfg = configDe(raiz);
    var ahora = new Date();

    if (esFecha) {
      // Antes de juzgar la fecha, el rango se pone al día: puede haberse
      // cruzado la hora de corte con la pestaña abierta.
      ponerRango(raiz, cfg, ahora);
      // Cambiar el día rehace las franjas: las de hoy no son las de
      // mañana.
      pintarFranjas(raiz, cfg, ahora);
    }

    var detalle = refrescar(raiz, cfg, ahora);
    if (detalle) guardar(detalle);
  });

  // ---- Arranque y pasos que llegan después

  function arrancar() {
    iniciarTodos();

    // El panel del carrito se reemplaza entero al agregar un producto y
    // cada vez que el theme recalcula totales.
    var pendiente = false;
    new MutationObserver(function () {
      if (pendiente) return;
      pendiente = true;
      requestAnimationFrame(function () {
        pendiente = false;
        iniciarTodos();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arrancar);
  } else {
    arrancar();
  }

  // El editor de temas reemplaza secciones sin recargar la página.
  document.addEventListener('shopify:section:load', function (evento) {
    iniciarTodos(evento.target);
  });
})();
